/**
 * CookieConsentContext
 *
 * Stores consent in localStorage key "ateno_cookie_consent".
 * Shape: { essential: true, analytics: bool, version: "1", savedAt: ISO }
 *
 * Also exposes `openBanner()` so the Cookie Policy page can let users
 * re-open the preferences modal at any time.
 */
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import consentApi from "../services/consent.service";

const STORAGE_KEY  = "ateno_cookie_consent";
const CONSENT_VER  = "1";

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [consent, setConsent]     = useState(null);   // null = not yet decided
  const [bannerOpen, setBannerOpen] = useState(false);

  // Load from localStorage on mount, then fall back to backend consent by IP.
  useEffect(() => {
    let active = true;
    let bannerTimer = null;

    const applyStoredConsent = (record) => {
      setConsent(record);
      setBannerOpen(false);
    };

    const openBannerLater = () => {
      if (bannerTimer) clearTimeout(bannerTimer);
      bannerTimer = setTimeout(() => {
        if (active) setBannerOpen(true);
      }, 800);
    };

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Re-prompt if policy version changed
        if (parsed.version === CONSENT_VER) {
          applyStoredConsent(parsed);
          return undefined;
        }
      }
    } catch { /* corrupt storage — ignore */ }

    consentApi
      .getConsent()
      .then((result) => {
        if (!active) return;
        const remoteConsent = result?.consent;
        if (remoteConsent?.version === CONSENT_VER) {
          const record = {
            essential: remoteConsent.essential !== false,
            analytics: remoteConsent.analytics === true,
            version: remoteConsent.version,
            savedAt: remoteConsent.savedAt || new Date().toISOString(),
          };
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(record)); } catch { /* private mode */ }
          applyStoredConsent(record);
          return;
        }
        openBannerLater();
      })
      .catch(() => {
        if (!active) return;
        openBannerLater();
      });

    return () => {
      active = false;
      if (bannerTimer) clearTimeout(bannerTimer);
    };
  }, []);

  /** Persist consent locally and send audit log to backend (fire-and-forget). */
  const saveConsent = useCallback((prefs) => {
    const record = {
      essential: true,
      analytics: Boolean(prefs.analytics),
      version:   CONSENT_VER,
      savedAt:   new Date().toISOString(),
    };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(record)); } catch { /* private mode */ }
    setConsent(record);
    setBannerOpen(false);

    // Audit log — best-effort
    consentApi
      .saveConsent({
        essential: record.essential,
        analytics: record.analytics,
        version: record.version,
        userAgent: navigator.userAgent,
      })
      .catch(() => {});
  }, []);

  const acceptAll     = useCallback(() => saveConsent({ analytics: true }),  [saveConsent]);
  const rejectAll     = useCallback(() => saveConsent({ analytics: false }), [saveConsent]);
  const openBanner    = useCallback(() => setBannerOpen(true),  []);
  const closeBanner   = useCallback(() => setBannerOpen(false), []);

  return (
    <CookieConsentContext.Provider value={{
      consent,
      bannerOpen,
      openBanner,
      closeBanner,
      acceptAll,
      rejectAll,
      saveConsent,
    }}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent must be used inside CookieConsentProvider");
  return ctx;
}
